import React from 'react';
import Modal from '../../../Components/Modal';
import Grid from '../../../Components/Formatting/Grid';
import NoValue from '../../../Components/EmptyStates/NoValue';
import Money from '../../../Components/Formatting/Money';
import TimeAgo from '../../../Components/Formatting/TimeAgo';
import Section, { Sections } from '../../../Components/Containers/Section';
import CopyableValue from '../../../Components/Formatting/CopyableValue';
import Address from '../../../Components/Formatting/Address';
import { classNames, numFormat } from '../../../Services/helpers';
import Icon from '../../../Components/Icons/TheIcon';
import { VerificationBadge } from '../../Verification';

const BusinessInfo = ({isOpen, setIsOpen, business}) => {
    return <Modal title={business.name} isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        {isOpen && <Inner business={business} />}
    </Modal>
}

const Inner = ({business}) => {
    return <Sections>
        <Section title="User">
            <Grid items={[
                [
                    <strong>User</strong>,
                    business?.user?.firstName ? `${business?.user?.firstName} ${business.user?.lastName}` : <NoValue />
                ],
                [
                    <strong>Email</strong>,
                    business.user?.email ? <CopyableValue value={business.user?.email} flush /> : <NoValue />
                ],
                [
                    <strong>Phone</strong>,
                    business?.phoneNumber ? <CopyableValue value={business?.phoneNumber} flush /> : <NoValue />
                ],
                [
                    <strong>Registered</strong>,
                    <TimeAgo dateTime={business?.createdAt} />
                ],
                [
                    <strong>Last Active</strong>,
                    <TimeAgo dateTime={business?.user?.lastActive} />
                ]
            ]} />
        </Section>
        <Section title="Overview">
            <Grid items={[
                [
                    <strong>Plan</strong>,
                    business?.balance?.plan ?? <NoValue />
                ],
                [
                    <strong>Address</strong>,
                    <Address address={business.address} />
                ],
                [
                    <strong>Customers</strong>,
                    business?.customers?.count ?? <NoValue />
                ],
                [
                    <strong>Payments</strong>,
                    business?.paymentAccount?.setupProgress?.completed ? 'Setup' : <NoValue />
                ],
            ]} />
        </Section>
        <Section title="Verifications">
            <div className="grid grid-cols-4 items-center justify-between text-center">
                <div>
                    <strong>Email</strong><br />
                    <VerificationBadge status={business?.verification?.emailStatus} />
                </div>
                <div>
                    <strong>Phone</strong><br />
                    <VerificationBadge status={business?.verification?.phoneNumberStatus} />
                </div>
                <div>
                    <strong>Identity</strong><br />
                    <VerificationBadge status={business?.verification?.identityStatus} />
                </div>
                <div>
                    <strong>Address</strong><br />
                    <VerificationBadge status={business?.verification?.addressStatus} />
                </div>
            </div>
        </Section>
        <Section title="Balance">
            <Grid items={[
                [
                    <strong>Plan</strong>,
                    business?.balance?.plan ?? <NoValue />
                ],
                [
                    <strong>Balance</strong>,
                    <Money pence={business?.balance?.retainedPence} colour />
                ],
                [
                    <strong>SMS</strong>,
                    <>
                        {numFormat(business?.balance?.sms ?? 0)}
                        &nbsp;
                        {business?.balance?.autoTopUpSMS && <span className="text-muted">
                            (Auto Top Up: {numFormat(business?.balance?.autoTopUpSMS)})
                        </span>}
                    </>
                ],
                [
                    <strong>Plan Credits</strong>,
                    business?.balance?.planCredits ?? <NoValue />
                ],
            ]} />
        </Section>
        <Section title="IDs">
            <Grid items={[
                [
                    <strong>Business</strong>,
                    <CopyableValue value={business?.id} fontMono />
                ],
                [
                    <strong>User</strong>,
                    <CopyableValue value={business?.user?.id} fontMono />
                ],
                [
                    <strong>Balance</strong>,
                    <CopyableValue value={business?.balance?.id} fontMono />
                ],
                [
                    <strong>Payments</strong>,
                    <CopyableValue value={business?.paymentAccount?.id} fontMono />
                ],
                [
                    <strong>Marketplace</strong>,
                    <CopyableValue value={business?.subscription?.id} fontMono />
                ],
            ]} />
        </Section>
        <Section title="Onboarding">
            <dl className="grid grid-cols-4 gap-4">
                {business?.journey?.activity && Object.entries(business.journey.activity).map(([key, value]) => {
                    const upward = (''+value).indexOf('U') > -1;
                    return [
                        <dt key={key} className="font-bold capitalize overflow-hidden">{key}</dt>,
                        <dd key={key+value} className={classNames(
                            upward ? 'text-green-600 fill-green-600' : 'text-red-600 fill-red-600',
                        )}>
                            <Icon theme="solid" icon={upward ? 'CaretUp' : 'CaretDown'} className="mr-2" />
                            {(''+value).replace('U', '').replace('D', '')}
                        </dd>
                    ]
                })}
            </dl>
        </Section>
    </Sections>
}

export default BusinessInfo;
